import { useState } from 'react'
import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import Container from '@cloudscape-design/components/container'
import Flashbar from '@cloudscape-design/components/flashbar'
import FormField from '@cloudscape-design/components/form-field'
import Header from '@cloudscape-design/components/header'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Spinner from '@cloudscape-design/components/spinner'
import Textarea from '@cloudscape-design/components/textarea'
import type { FlashbarProps } from '@cloudscape-design/components/flashbar'
import Page from '@/components/common/Page'
import { useMyPipelineConfig, useUpsertMyPipelineConfig, useDeleteMyPipelineConfig } from '@/hooks/usePipelineConfig'
import type { ClientPipelineConfig, PipelinePrompts } from '@/lib/types'
import { formatDate } from '@/utils/format'

function getPrompts(config: ClientPipelineConfig | null): PipelinePrompts {
  if (!config?.config || typeof config.config !== 'object') return {}
  const c = config.config as Record<string, unknown>
  if (!c.prompts || typeof c.prompts !== 'object') return {}
  return c.prompts as PipelinePrompts
}

const PIPELINE = 'dme_outreach_v2'

export default function PromptsPage() {
  const { config, loading } = useMyPipelineConfig(PIPELINE)
  const { upsert } = useUpsertMyPipelineConfig()
  const { reset: resetConfig, loading: resetting } = useDeleteMyPipelineConfig()

  const [flash, setFlash] = useState<FlashbarProps.MessageDefinition[]>([])
  const [showConfirm, setShowConfirm] = useState(false)
  const [generateDraft, setGenerateDraft] = useState(() => getPrompts(config).generate_system ?? '')
  const [validateDraft, setValidateDraft] = useState(() => getPrompts(config).validate_system ?? '')
  const [savingField, setSavingField] = useState<'generate' | 'validate' | null>(null)

  const formattedDate = config?.updatedAt ? formatDate(config.updatedAt) : null

  const clearFlash = () => setFlash([])

  const handleSave = async (field: 'generate_system' | 'validate_system', value: string) => {
    const currentPrompts = getPrompts(config)
    setSavingField(field === 'generate_system' ? 'generate' : 'validate')
    try {
      await upsert(PIPELINE, { prompts: { ...currentPrompts, [field]: value } })
      setFlash([{ type: 'success', content: 'Prompt saved.', dismissible: true, onDismiss: clearFlash }])
    } catch {
      setFlash([{ type: 'error', content: 'Failed to save prompt.', dismissible: true, onDismiss: clearFlash }])
    } finally {
      setSavingField(null)
    }
  }

  const showResetConfirmHandler = () => setShowConfirm(true)
  const cancelReset = () => setShowConfirm(false)

  const confirmReset = async () => {
    try {
      await resetConfig(PIPELINE)
      setShowConfirm(false)
      setFlash([{ type: 'success', content: 'Prompts reset to system defaults.', dismissible: true, onDismiss: clearFlash }])
    } catch {
      setFlash([{ type: 'error', content: 'Failed to reset prompts.', dismissible: true, onDismiss: clearFlash }])
    }
  }

  return (
    <Page
      title="Prompts"
      helpContent={{
        header: 'System Prompts',
        body: (
          <div>
            <p>Fine-tune the AI prompts that guide content generation and validation. For advanced users.</p>
            <h4>Available Prompts:</h4>
            <ul style={{ marginLeft: 20 }}>
              <li><strong>Generate Prompt</strong> - Instructions for creating content pieces</li>
              <li><strong>Validate Prompt</strong> - Instructions for quality checking content</li>
            </ul>
            <h4>Advanced Tips:</h4>
            <p>Edit these prompts to change how the AI generates and evaluates content. Changes apply to all future generations. You can reset to defaults anytime.</p>
          </div>
        ),
      }}
    >
      <Container>
        <SpaceBetween key={config?.updatedAt ?? 'loading'} direction="vertical" size="l">
        <Flashbar items={flash} />
        <Box>
          <Header
            variant="h2"
            description="Override the default AI prompts for your pipeline."
            actions={
              <SpaceBetween direction="horizontal" size="s">
                {formattedDate !== null && (
                  <Box fontSize="body-s" color="text-status-inactive">
                    Last saved: {formattedDate}
                  </Box>
                )}
                <Button
                  variant="normal"
                  iconName="undo"
                  loading={resetting}
                  onClick={showResetConfirmHandler}
                >
                  Reset to default
                </Button>
              </SpaceBetween>
            }
          >
            Configuration
          </Header>
        </Box>

        {showConfirm && (
          <Box color="text-status-warning" fontSize="body-s">
            <SpaceBetween direction="horizontal" size="s">
              <Box variant="span">This will remove all prompt overrides and revert to system defaults.</Box>
              <Button variant="link" onClick={cancelReset}>Cancel</Button>
              <Button variant="normal" onClick={() => { void confirmReset() }} loading={resetting}>Confirm reset</Button>
            </SpaceBetween>
          </Box>
        )}

        <Container header={<Header variant="h3">Generate prompt</Header>}>
          {loading ? (
            <Box textAlign="center"><Spinner size="normal" /></Box>
          ) : (
            <SpaceBetween direction="vertical" size="m">
              <FormField
                label="System prompt"
                description={
                  generateDraft === ''
                    ? (
                      <Box fontSize="body-s" color="text-body-secondary">
                        No override set — the system default will be used.
                      </Box>
                    )
                    : undefined
                }
              >
                <Textarea
                  rows={8}
                  placeholder="Enter a custom generate system prompt…"
                  value={generateDraft}
                  onChange={(e) => setGenerateDraft(e.detail.value)}
                />
              </FormField>
              <Box textAlign="right">
                <Button
                  variant="primary"
                  loading={savingField === 'generate'}
                  onClick={() => { void handleSave('generate_system', generateDraft) }}
                >
                  Save
                </Button>
              </Box>
            </SpaceBetween>
          )}
        </Container>

        <Container header={<Header variant="h3">Validate prompt</Header>}>
          {loading ? (
            <Box textAlign="center"><Spinner size="normal" /></Box>
          ) : (
            <SpaceBetween direction="vertical" size="m">
              <FormField
                label="System prompt"
                description={
                  validateDraft === ''
                    ? (
                      <Box fontSize="body-s" color="text-body-secondary">
                        No override set — the system default will be used.
                      </Box>
                    )
                    : undefined
                }
              >
                <Textarea
                  rows={8}
                  placeholder="Enter a custom validate system prompt…"
                  value={validateDraft}
                  onChange={(e) => setValidateDraft(e.detail.value)}
                />
              </FormField>
              <Box textAlign="right">
                <Button
                  variant="primary"
                  loading={savingField === 'validate'}
                  onClick={() => { void handleSave('validate_system', validateDraft) }}
                >
                  Save
                </Button>
              </Box>
            </SpaceBetween>
          )}
        </Container>
        </SpaceBetween>
      </Container>
    </Page>
  )
}
